import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import TritalLogo from "../../components/TritalLogo";
import { useAuth } from "../../context/AuthContext";
import { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Stethoscope, Building2, UserSearch } from "lucide-react";
import SMSConsentBlock from "../../components/SMSConsentBlock";

const SPECIALTIES = ["ICU", "ER", "Med-Surg", "OR", "Cath Lab", "Respiratory Therapy", "Physical Therapy", "Radiology", "Labor & Delivery", "Telemetry", "Oncology"];
const STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialRole = params.get("role") || "clinician";
  const [role, setRole] = useState(initialRole);
  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "",
    specialty: "ICU", license_state: "TX", years_experience: 1,
    facility_name: "", facility_type: "Hospital", city: "", state: "TX",
  });
  const [smsOptIn, setSmsOptIn] = useState(false); // TCPA: SMS consent state
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const payload = { 
        ...form, 
        role, 
        years_experience: Number(form.years_experience),
        sms_opt_in: smsOptIn  // TCPA: Include SMS consent
      };
      const user = await register(payload);
      toast.success(`Welcome, ${user.name}`);
      nav("/dashboard");
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setLoading(false);
    }
  };

  const roleCards = [
    { key: "clinician", Icon: Stethoscope, t: "Clinician", d: "Nurses, techs, therapists" },
    { key: "facility", Icon: Building2, t: "Facility", d: "Hospitals & clinics" },
    { key: "recruiter", Icon: UserSearch, t: "Recruiter", d: "Agency / MSP" },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="register-page">
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-10">
        <Link to="/"><TritalLogo /></Link>
        <div className="mt-10 grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2">
            <div className="overline text-trital-red">Join TRITAL Care®</div>
            <h1 className="mt-2 font-display font-black text-4xl lg:text-5xl text-slate-900 tracking-tight">Create your account.</h1>
            <p className="mt-3 text-slate-600">Choose your role to personalize your dashboard.</p>
            <div className="mt-6 space-y-3">
              {roleCards.map(({ key, Icon, t, d }) => (
                <button key={key} type="button" onClick={() => setRole(key)} className={`w-full text-left p-4 rounded-2xl border transition-all ${role === key ? "border-trital-navy bg-trital-medical-blue-soft/40 shadow-card" : "border-slate-200 bg-white hover:border-slate-300"}`} data-testid={`role-${key}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${role === key ? "bg-trital-navy text-white" : "bg-slate-100 text-slate-700"}`}><Icon className="w-5 h-5" /></div>
                    <div>
                      <div className="font-display font-bold text-slate-900">{t}</div>
                      <div className="text-sm text-slate-600">{d}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
          <div className="lg:col-span-3">
            <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-200 p-7 shadow-card">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 md:col-span-1">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Full name</label>
                  <input required value={form.name} onChange={upd("name")} className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-trital-navy/30 focus:border-trital-navy outline-none" data-testid="reg-name" />
                </div>
                <div className="col-span-2 md:col-span-1">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Email</label>
                  <input required type="email" value={form.email} onChange={upd("email")} className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-trital-navy/30 focus:border-trital-navy outline-none" data-testid="reg-email" />
                </div>
                <div className="col-span-2 md:col-span-1">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Password</label>
                  <input required type="password" minLength={6} value={form.password} onChange={upd("password")} className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-trital-navy/30 focus:border-trital-navy outline-none" data-testid="reg-password" />
                </div>
                <div className="col-span-2 md:col-span-1">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Phone</label>
                  <input value={form.phone} onChange={upd("phone")} className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-trital-navy/30 focus:border-trital-navy outline-none" data-testid="reg-phone" />
                </div>
                {role === "clinician" && (
                  <>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Specialty</label>
                      <select value={form.specialty} onChange={upd("specialty")} className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-trital-navy/30 focus:border-trital-navy outline-none" data-testid="reg-specialty">
                        {SPECIALTIES.map((s) => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-slate-600">License state</label>
                      <select value={form.license_state} onChange={upd("license_state")} className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-trital-navy/30 focus:border-trital-navy outline-none" data-testid="reg-state">
                        {STATES.map((s) => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Years experience</label>
                      <input type="number" min={0} value={form.years_experience} onChange={upd("years_experience")} className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-trital-navy/30 focus:border-trital-navy outline-none" data-testid="reg-years" />
                    </div>
                  </>
                )}
                {role === "facility" && (
                  <>
                    <div className="col-span-2">
                      <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Facility name</label>
                      <input required value={form.facility_name} onChange={upd("facility_name")} className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-trital-navy/30 focus:border-trital-navy outline-none" data-testid="reg-facility-name" />
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Facility type</label>
                      <select value={form.facility_type} onChange={upd("facility_type")} className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-trital-navy/30 focus:border-trital-navy outline-none">
                        {["Hospital", "Clinic", "Skilled Nursing", "Urgent Care", "Home Health", "Rehab"].map((s) => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-slate-600">State</label>
                      <select value={form.state} onChange={upd("state")} className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-trital-navy/30 focus:border-trital-navy outline-none">
                        {STATES.map((s) => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs font-bold uppercase tracking-wider text-slate-600">City</label>
                      <input value={form.city} onChange={upd("city")} className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-trital-navy/30 focus:border-trital-navy outline-none" />
                    </div>
                  </>
                )}
              </div>
              
              {/* TCPA: SMS Consent Block — ALWAYS visible (SimpleTexting requirement) */}
              <div className="mt-5">
                <SMSConsentBlock
                  checked={smsOptIn}
                  onCheckedChange={setSmsOptIn}
                  checkboxId="register-sms-consent"
                />
              </div>
              
              {err && <div className="mt-4 text-sm text-trital-red bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="reg-error">{err}</div>}
              <button type="submit" disabled={loading} className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-trital-navy text-white font-semibold btn-lift hover:bg-trital-navy-deep disabled:opacity-60" data-testid="reg-submit">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />} Create account
              </button>
              <div className="mt-3 text-sm text-slate-600">Already have an account? <Link to="/login" className="text-trital-navy font-semibold hover:underline">Sign in</Link></div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
